import cloneDeep from 'lodash.clonedeep';
import { numericFormatter } from 'react-number-format';

export const copy = (obj) => cloneDeep(obj);

/** Normalize text for @react-pdf searchable/copyable output (avoid soft hyphens / fancy dashes). */
export const sanitizePdfText = (value) => {
  if (value === null || value === undefined) return '';
  const removeCodes = new Set([0x00AD, 0x200B, 0x200C, 0x200D, 0xFEFF]);
  const dashCodes = new Set([0x2010, 0x2011, 0x2012, 0x2013, 0x2014, 0x2212]);
  const singleQuoteCodes = new Set([0x2018, 0x2019, 0x201A]);
  const doubleQuoteCodes = new Set([0x201C, 0x201D, 0x201E]);
  let output = '';
  const text = String(value);
  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i);
    if (removeCodes.has(code)) {
      // skip
    } else if (dashCodes.has(code)) {
      output += '-';
    } else if (singleQuoteCodes.has(code)) {
      output += "'";
    } else if (doubleQuoteCodes.has(code)) {
      output += '"';
    } else if (code === 0x2026) {
      output += '...';
    } else if (code === 0x00A0) {
      output += ' ';
    } else {
      output += text[i];
    }
  }
  return output;
};

// anystring@anystring.anystring
export const isAValidEmail = (str) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(str);
// check for valid (xxx) xxx-xxxx phone US phone number format
export const isAValidUSPhoneNumber = (str) => /^\(?([0-9]{3})\)?[ ]?([0-9]{3})[-]?([0-9]{4})$/.test(str);

export function checkForEmailErrors(email) {
  let errorStatus = true;
  let errorMessage = '';
  if ((!email.includes('@') || email.split('@').length > 2)) errorMessage = 'An email address must contain a single @';
  else if (!email.split('@')[1].includes('.')) errorMessage = 'The domain portion of the email address is invalid (the portion after @)';
  else if (!isAValidEmail(email)) errorMessage = 'Invalid email entered...';
  else errorStatus = false;
  return { errorStatus, errorMessage };
}

export const REDWOOD_ENTERPRISE_EMAIL_DOMAIN = '@redwoodvaluation.com';

export function checkForRedwoodEnterpriseEmailErrors(email) {
  const base = checkForEmailErrors(email);
  if (base.errorStatus) return base;

  const normalized = email.trim().toLowerCase();
  if (!normalized.endsWith(REDWOOD_ENTERPRISE_EMAIL_DOMAIN)) {
    return {
      errorStatus: true,
      errorMessage: 'Only @redwoodvaluation.com email addresses can be added.',
    };
  }
  return { errorStatus: false, errorMessage: '' };
}

export function formatPhoneNumber(value) {
  // allow only digit values
  const phoneNumber = value.replace(/[^\d]/g, '');

  // return the value with no formatting if it is less than four digits
  // avoid confusing behavior when functions formats the area code too early
  if (phoneNumber.length < 4) return phoneNumber;

  // if phone number length is greater than 4 and less the 7 return first formatting
  if (phoneNumber.length < 7) return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;

  // finally, if the phone number length is greater then 7, add the rest of the formatting
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
}

export function timeOfDayGreeting() {
  const today = new Date();
  const curHr = today.getHours();

  if (curHr < 12) return 'morning';
  if (curHr < 18) return 'afternoon';

  return 'evening';
}

export function truncateStr(string = '', maxLength = 50) {
  if (string.length > maxLength) {
    return `${string.substring(0, maxLength)}...`;
  }
  return string;
}

export const onlyNums = (str) => str.replace(/[^\d.]/g, '');

export const onlyDates = (str) => str.replace(/[^\d./-]/g, '');

export const commaEvery3rdChar = (val) => numericFormatter((val || '').toString().replace(/[^\d.-]/g, ''), { thousandSeparator: true });

export const removeCommas = (str) => str.replaceAll(',', '');

export const stripDenomination = (str) => str.replace(/[$,]/g, '');

export function getInitials(name) {
  if (!name) return name;
  const splitName = name.split(' ');
  const firstInitial = Array.from(splitName[0])[0];
  const lastInitial = splitName[1] ? Array.from(splitName[1])[0] : '';
  return firstInitial + lastInitial;
}

export const concatCharacter = (input, char) => (input && (!input.toString().includes(char) ? input.toString() + char : input));

export function formalizeClassName(name) {
  if (name == null || name === '') return name;
  const text = String(name);
  const trimmed = text.trim();
  if (/\bPreferred$/i.test(trimmed) && !/\bPreferred Stock$/i.test(trimmed)) {
    return `${trimmed} Stock`;
  }
  return text;
}

export const formatSeriesName = (seriesName) => {
  if (seriesName == null || seriesName === '') return seriesName;
  let name = String(seriesName);
  if (name.includes(' @ $')) {
    const parts = name.split(' @ $');
    const strike = parts[parts.length - 1];
    name = `${parts[0]} @ $${new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    }).format(strike)}`;
  }
  return formalizeClassName(name);
};

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function legalNameAliases(legalName) {
  const full = String(legalName || '').trim();
  if (!full) return [];
  const aliases = [full];
  const stripped = full
    .replace(/[.,\s]+$/g, '')
    .replace(/,?\s+(Inc\.?|Incorporated|Ltd\.?|Limited|LLC|L\.L\.C\.?|Corp\.?|Corporation|Co\.?|PLC|P\.L\.C\.?|LP|L\.P\.?|LLP|P\.C\.?|PC)\.?$/i, '')
    .replace(/[.,\s]+$/g, '')
    .trim();
  if (stripped && stripped.toLowerCase() !== full.toLowerCase()) aliases.push(stripped);
  return aliases;
}

export function applyDbaToDescription(description, legalName, dba) {
  if (!description || !dba) return description;
  const dbaName = String(dba).trim();
  if (!dbaName) return description;

  const already = new RegExp(`doing business as\\s+${escapeRegExp(dbaName)}\\b`, 'i');
  if (already.test(description)) return description;

  const matchedName = legalNameAliases(legalName)
    .filter(Boolean)
    .find((name) => {
      const re = new RegExp(`doing business as\\s+${escapeRegExp(name)}(?=[,.;]|\\s|$)`, 'i');
      return re.test(description);
    });

  if (matchedName) {
    const re = new RegExp(`doing business as\\s+${escapeRegExp(matchedName)}(?=[,.;]|\\s|$)`, 'i');
    return description.replace(re, `doing business as ${dbaName}`);
  }
  return description;
}

// limit the number of characters and numerical value of a given string
export function maxCharsAndValue(str, charLimit, valLimit) {
  let newStr = str;
  if (parseInt(newStr, 10) > valLimit) newStr = valLimit.toString();
  if (newStr.length > charLimit) newStr.substring(0, charLimit);
  return newStr;
}

export const formatUSCurrency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumSignificantDigits: 2 });

export const formatUSCurrencyWithoutDollarSign = new Intl.NumberFormat('en-US', {
  style: 'decimal',
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
});

export const formatUSCurrencyWithTwoDecimals = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
});

export const formatUSCurrencyWithFourDecimals = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 4,
  minimumFractionDigits: 4,
});

export const formatUSCurrencyWithSixDecimals = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 6,
  minimumFractionDigits: 6,
});

export function toTitleCase(str) {
  return str
    .toLowerCase() // Convert the whole string to lowercase
    .replace(/(^|\s)\w/g, (match) => match.toUpperCase()); // Capitalize the first letter of each word
}

export function toCamelCase(inputString) {
  return inputString
    .toLowerCase()
    .replace(/[^a-zA-Z0-9]+(.)/g, (_, chr) => chr.toUpperCase());
}

export function millisecondsToStr(milliseconds) {
  let temp = Math.floor(milliseconds / 1000);
  const years = Math.floor(temp / 31536000);
  if (years) return `${years} year${years > 1 ? 's' : ''}`;
  const days = Math.floor((temp %= 31536000) / 86400);
  if (days) return `${days} day${days > 1 ? 's' : ''}`;
  const hours = Math.floor((temp %= 86400) / 3600);
  if (hours) return `${hours} hour${hours > 1 ? 's' : ''}`;
  const minutes = Math.floor((temp %= 3600) / 60);
  if (minutes) return `${minutes} minute${minutes > 1 ? 's' : ''}`;
  const seconds = temp % 60;
  if (seconds) return `${seconds} second${seconds > 1 ? 's' : ''}`;
  return 'less than a second';
}


export default {
  copy,
  isAValidEmail,
  isAValidUSPhoneNumber,
  checkForEmailErrors,
  checkForRedwoodEnterpriseEmailErrors,
  REDWOOD_ENTERPRISE_EMAIL_DOMAIN,
  formatPhoneNumber,
  timeOfDayGreeting,
  onlyNums,
  onlyDates,
  commaEvery3rdChar,
  formatSeriesName,
  formalizeClassName,
  applyDbaToDescription,
  removeCommas,
  stripDenomination,
  getInitials,
  concatCharacter,
  maxCharsAndValue,
  formatUSCurrency,
  formatUSCurrencyWithTwoDecimals,
  formatUSCurrencyWithFourDecimals,
  toTitleCase,
  toCamelCase,
  truncateStr,
  millisecondsToStr,
};
